import { Controller } from "@hotwired/stimulus"

// Reconstructs and plays back one SessionReplay recording (see app/models/session_replay.rb):
// fetches the gzip blob directly (urlValue is its plain rails_blob_path, the same file a "Download"
// link would point at), decompresses it client-side with the same DecompressionStream API
// sdks/typescript's own recorder used to compress it, then replays the snapshot/mutation/click/
// mousemove/scroll events it carries into a sandboxed iframe, at a chosen playback speed.
//
// sandbox="allow-same-origin" only, deliberately never allow-scripts: this needs same-origin
// access to the iframe's own contentDocument to build the reconstructed DOM into it, but nothing
// reconstructed here should ever actually execute. An inline onclick attribute or a javascript:
// href, if a recorded page happened to carry one, would be inert in a sandbox with no script
// execution allowed at all, and buildNode below strips both before they ever reach the frame
// anyway, along with anything that would load from the network or navigate; see its own comment
// for everything it removes and why a recording can't be trusted to leave those out itself.
//
// Rebuilds the whole DOM from the snapshot and replays every event up to the target time on every
// seek (including each tick of ordinary forward playback), rather than incrementally applying or
// undoing individual mutations: simpler, and correct by construction (there's no risk of drifting
// out of sync with the recording), and cheap enough not to matter at this recording's own bounded
// size (see sessionReplay.ts's own MAX_BUFFERED_EVENTS/REBASELINE_INTERVAL_MS).
export default class extends Controller {
  static values = { url: String, speed: { type: Number, default: 1 } }
  static targets = [
    "loadButton", "player", "frame", "cursor", "scrubber", "playButton", "speedButton", "elapsed", "duration", "error",
  ]

  connect() {
    this.recording = null
    this.nodesById = null
    this.playing = false
    this.currentMs = 0
    this.lastTickAt = null
    this.timer = null
  }

  disconnect() {
    this.stop()
  }

  async load() {
    this.loadButtonTarget.disabled = true
    this.loadButtonTarget.textContent = "Loading…"

    try {
      this.recording = await this.fetchAndDecode()
      this.durationTarget.textContent = formatMs(this.recording.durationMs)
      this.scrubberTarget.max = String(this.recording.durationMs)
      this.loadButtonTarget.classList.add("d-none")
      this.playerTarget.classList.remove("d-none")
      this.seekTo(0)
      this.play()
    } catch (e) {
      this.errorTarget.textContent = `Couldn't load this recording: ${e.message}`
      this.errorTarget.classList.remove("d-none")
      this.loadButtonTarget.disabled = false
      this.loadButtonTarget.textContent = "Watch session replay recording"
    }
  }

  togglePlay() {
    if (this.playing) this.pause()
    else this.play()
  }

  play() {
    if (this.playing) return
    // Already at (or past) the end: restart from the beginning rather than a no-op "play"
    // button that visibly does nothing.
    if (this.currentMs >= this.recording.durationMs) this.currentMs = 0

    this.playing = true
    this.playButtonTarget.textContent = "Pause"
    this.lastTickAt = performance.now()
    this.timer = setInterval(() => this.tick(), 100)
  }

  pause() {
    this.playing = false
    this.playButtonTarget.textContent = "Play"
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  stop() {
    this.pause()
  }

  seek(event) {
    this.pause()
    this.seekTo(Number(event.target.value))
  }

  tick() {
    const now = performance.now()
    const elapsedSinceLastTick = (now - this.lastTickAt) * this.speedValue
    this.lastTickAt = now

    this.seekTo(this.currentMs + elapsedSinceLastTick)
    if (this.currentMs >= this.recording.durationMs) this.pause()
  }

  // Reads event.params.speed (a data-session-replay-player-speed-param on each speed button in
  // the view), not event.target.value: a plain button group, not a <select>, so a currently-
  // active speed can get its own visual state via a CSS class instead of relying on whatever the
  // browser's own native styling does for a selected <option>.
  setSpeed(event) {
    this.speedValue = event.params.speed
    for (const button of this.speedButtonTargets) {
      button.classList.toggle("active", Number(button.dataset.sessionReplayPlayerSpeedParam) === this.speedValue)
    }
  }

  seekTo(ms) {
    const previousMs = this.currentMs
    this.currentMs = Math.max(0, Math.min(ms, this.recording.durationMs))
    this.scrubberTarget.value = String(this.currentMs)
    this.elapsedTarget.textContent = formatMs(this.currentMs)
    this.render(previousMs)
  }

  // The one place the frame's own document actually gets rebuilt; called on every seek, so this
  // has to stay cheap. See this class's own top comment for why rebuilding from scratch each time
  // (rather than incrementally applying/undoing mutations) is the deliberate, simpler choice here.
  //
  // previousMs is where playback was immediately before this call, needed for exactly one thing:
  // a click marker (see showClickMarker) is a one-off flash, not a lasting change to the page the
  // way every other event here is, so it only fires for a click actually newly crossed since the
  // last render, never replayed again on every later tick just because the full mutation history
  // is (harmlessly, idempotently) replayed from scratch every time.
  render(previousMs) {
    const frameDocument = this.frameTarget.contentDocument
    this.nodesById = new Map()
    let documentElement = buildNode(frameDocument, this.recording.snapshot, this.nodesById)
    // A root buildNode refused (see BLOCKED_TAGS) comes back as a comment, which can't stand in
    // for the document element; an empty page keeps the next render's replaceWith working.
    if (documentElement.nodeType !== Node.ELEMENT_NODE) documentElement = frameDocument.createElement("html")
    frameDocument.documentElement.replaceWith(documentElement)

    let scrollTarget = null
    let cursorPosition = null
    const seekedForward = this.currentMs >= previousMs
    for (const event of this.recording.events) {
      if (event.t > this.currentMs) break

      switch (event.type) {
        case "childList":
        case "attributes":
        case "characterData":
          this.applyMutation(event)
          break
        case "scroll":
          scrollTarget = event
          break
        case "mousemove":
          cursorPosition = event
          break
        case "click":
          // Newly crossed since last render only (see this method's own comment); a backward
          // scrub past an old click shouldn't flash it again either, seekedForward guards that too.
          if (seekedForward && event.t > previousMs) this.showClickMarker(event.x, event.y)
          break
        // resize: not visually applied; the player's own iframe stays a fixed size regardless of
        // what the real page's own viewport did during the recording.
      }
    }
    // Both applied once, after every mutation up to this point has already landed, not inline as
    // each is encountered above: a scroll position or cursor location only means anything once
    // the content it refers to is actually in the document.
    if (scrollTarget) this.frameTarget.contentWindow.scrollTo(scrollTarget.x, scrollTarget.y)
    this.updateCursor(cursorPosition)
  }

  applyMutation(event) {
    switch (event.type) {
      case "childList": {
        const target = this.nodesById.get(event.targetId)
        if (!target) return
        for (const removedId of event.removedIds) {
          const removed = this.nodesById.get(removedId)
          if (removed && removed.parentNode === target) target.removeChild(removed)
        }
        // A text node can't take children; a hand-built recording could still target one.
        if (target.nodeType !== Node.ELEMENT_NODE) return
        const parentTag = target.tagName.toLowerCase()
        for (const addedSnapshot of event.added) {
          target.appendChild(buildNode(this.frameTarget.contentDocument, addedSnapshot, this.nodesById, parentTag))
        }
        return
      }
      case "attributes": {
        const target = this.nodesById.get(event.targetId)
        if (!target || target.nodeType !== Node.ELEMENT_NODE) return
        if (event.value === null) target.removeAttribute(event.name)
        else setSafeAttribute(target, event.name, event.value)
        return
      }
      case "characterData": {
        const target = this.nodesById.get(event.targetId)
        if (!target) return
        const insideStyle = target.parentNode?.nodeName?.toLowerCase() === "style"
        target.textContent = insideStyle ? sanitizeCss(event.text) : event.text
        return
      }
    }
  }

  // Shows a persistent cursor at the most recent mousemove position at or before the current
  // playback time, not a fading marker the way a click gets: unlike a click, "the cursor is
  // resting here" is true for the whole stretch until the next mousemove sample, not a single
  // instant. Hidden entirely before the recording's first mousemove sample, rather than guessing
  // a starting position nothing in the recording actually establishes.
  updateCursor(position) {
    if (!position) {
      this.cursorTarget.classList.add("d-none")
      return
    }
    this.cursorTarget.classList.remove("d-none")
    this.cursorTarget.style.left = `${position.x}px`
    this.cursorTarget.style.top = `${position.y}px`
  }

  // A brief, fading dot at the recorded click's own coordinates, appended to (and removed from)
  // the player's own overlay, not the iframe's reconstructed document: a click is a moment in
  // time, not a lasting part of the page, so it has no business surviving the next render() call
  // the way an actual DOM mutation does.
  showClickMarker(x, y) {
    const marker = document.createElement("div")
    marker.className = "session-replay-click-marker"
    marker.style.left = `${x}px`
    marker.style.top = `${y}px`
    this.frameTarget.parentElement.appendChild(marker)
    setTimeout(() => marker.remove(), 400)
  }

  async fetchAndDecode() {
    const response = await fetch(this.urlValue)
    if (!response.ok) throw new Error(`server responded ${response.status}`)

    const bytes = new Uint8Array(await response.arrayBuffer())
    // A ReadableStream built directly from the bytes, not Blob#stream(): see
    // sdks/typescript/src/sessionReplay.ts's own gzipAndEncode for why this app avoids
    // Blob#stream() in favor of this more consistently-supported path across browsers.
    const readable = new ReadableStream({
      start(controller) {
        controller.enqueue(bytes)
        controller.close()
      },
    })
    const decompressed = readable.pipeThrough(new DecompressionStream("gzip"))
    const json = await new Response(decompressed).text()
    const parsed = JSON.parse(json)

    const durationMs = parsed.events.length ? parsed.events[parsed.events.length - 1].t : 0
    return { snapshot: parsed.snapshot, events: parsed.events, durationMs }
  }
}

// Mirrors sdks/typescript/src/sessionReplay.ts's own SerializedNode shape exactly: this player
// only ever renders what that recorder actually produced, id included, so a later mutation event
// referencing a targetId can look the node back up in nodesById.
//
// Everything in a recording is untrusted: the ingestion key that uploads one ships inside the
// customer's own public JavaScript, so anyone can upload a "recording" built by hand. Beyond the
// sandbox's own no-scripts rule, nothing reconstructed here is allowed to load anything from the
// network or navigate anywhere either: elements whose whole purpose is loading or running
// something (BLOCKED_TAGS) are left out entirely, event handler attributes are dropped, every
// URL-bearing attribute is dropped unless it's an inline data: image, and every url() in inline
// CSS is replaced. What a normal recording is actually made of (structure, text, inline styles)
// replays exactly as before. The iframe's own csp attribute (see issues/show.html.erb) is the
// second, independent layer under this one.
function buildNode(doc, node, nodesById, parentTag = null) {
  if (node.type === "text") {
    const text = parentTag === "style" ? sanitizeCss(node.text) : node.text
    const textNode = doc.createTextNode(text)
    nodesById.set(node.id, textNode)
    return textNode
  }

  const tag = String(node.tag || "").toLowerCase()
  let element = null
  if (!BLOCKED_TAGS.has(tag)) {
    // A malformed tag name throws, the same as a malformed attribute name below; either way the
    // node is replaced by an inert, empty comment, never registered in nodesById, so a later
    // mutation targeting it simply finds nothing.
    try {
      element = doc.createElement(tag)
    } catch {
      element = null
    }
  }
  if (!element) return doc.createComment("")

  for (const [name, value] of Object.entries(node.attrs || {})) {
    setSafeAttribute(element, name, value)
  }
  nodesById.set(node.id, element)
  for (const child of node.children || []) {
    element.appendChild(buildNode(doc, child, nodesById, tag))
  }
  return element
}

// Elements that exist to load or run something (a script, another document, a plugin, a
// stylesheet from elsewhere) or to change how the rest of the document resolves URLs.
const BLOCKED_TAGS = new Set([
  "script", "noscript", "meta", "base", "iframe", "frame", "frameset", "object", "embed", "applet",
  "link", "portal", "template",
])

// Attributes whose value is a URL the browser would fetch or navigate to.
const URL_ATTRIBUTES = new Set([
  "src", "srcset", "href", "xlink:href", "action", "formaction", "poster", "background", "data",
  "codebase", "cite", "longdesc", "lowsrc", "dynsrc", "ping", "manifest", "imagesrcset", "srcdoc",
  "xml:base",
])

// Only a plain inline image survives in a URL attribute: it never touches the network, and it's
// the one kind of URL that matters for a replay to look like the page it came from.
const INLINE_IMAGE = /^\s*data:image\/(png|gif|jpe?g|webp|avif|bmp|x-icon|vnd\.microsoft\.icon);/i

// A malformed attribute name (nothing this app's own recorder would ever produce, but this is
// still someone else's recorded page content) throws rather than silently corrupting the rest of
// the reconstruction; skipped, the same graceful-degradation posture the rest of this app already
// takes with imperfect input. Shared by the snapshot and by "attributes" mutation events, so a
// later mutation can't reintroduce anything the snapshot was stripped of.
function setSafeAttribute(element, name, value) {
  const lowered = String(name).toLowerCase()
  if (lowered.startsWith("on")) return
  if (URL_ATTRIBUTES.has(lowered) && !((lowered === "src" || lowered === "poster") && INLINE_IMAGE.test(value))) return

  const safeValue = lowered === "style" ? sanitizeCss(value) : value
  try {
    element.setAttribute(name, safeValue)
  } catch {
    // Ignored; see comment above.
  }
}

// Every url(...), image-set(...), src(...), and @import in a style attribute or a <style> element
// replaced, so no stylesheet, font, or background image is ever fetched. CSS lets a function name
// be spelled with escapes ("u\72l(" is still url), so the cleaned text is checked once more with
// its escapes decoded, and dropped entirely if anything fetchable is still in it.
const CSS_FETCHES = /(?:url|image-set|-webkit-image-set|src)\s*\((?:"[^"]*"|'[^']*'|[^)]*)\)|@import[^;]*;?/gi
const CSS_FETCH_LEFT = /(?:url|image-set|src)\s*\(|@import/i

function sanitizeCss(css) {
  const cleaned = String(css ?? "").replace(CSS_FETCHES, "none")
  const unescaped = cleaned.replace(/\\([0-9a-f]{1,6}\s?|[^\n])/gi, (_, escape) => {
    const hex = escape.trim()
    return /^[0-9a-f]+$/i.test(hex) ? String.fromCodePoint(Math.min(parseInt(hex, 16), 0x10ffff) || 0xfffd) : escape
  })
  return CSS_FETCH_LEFT.test(unescaped) ? "" : cleaned
}

function formatMs(ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}
